import { ref, computed } from '@vue/composition-api'
import CustomerApi from '@/api/waiter/Customer'
import { handleApiError } from '@/utils/errorHandler'

export const SEGMENT_LABELS = {
  vip: '上顧客',
  loyal: '常連',
  new_vip_candidate: '有望株',
  at_risk: 'ご無沙汰',
  dormant: '休眠',
  regular: '一般',
}

export const SEGMENT_DESCRIPTIONS = {
  vip: '来店頻度が高く、利用金額も多い最重要顧客',
  loyal: '定期的に来店してくれる安定した顧客',
  new_vip_candidate: '最近来店し始めたが利用金額が高い、VIP候補の顧客',
  at_risk: '以前は来店していたが、最近来店が減っている顧客',
  dormant: '長期間来店がない顧客',
  regular: '一般的な来店パターンの顧客',
}

export const SEGMENT_COLORS = {
  vip: '#4CAF50',
  loyal: '#2196F3',
  new_vip_candidate: '#9C27B0',
  at_risk: '#FF9800',
  dormant: '#F44336',
  regular: '#9E9E9E',
}

export default function useRfmAnalysis(vm) {
  const customers = ref([])
  const summary = ref({
    totalCustomers: 0,
    analyzedCustomers: 0,
    averageRfmScore: 0,
  })
  const segments = ref([])
  const loading = ref(false)
  const startDate = ref(null)
  const endDate = ref(null)

  const hasCustomers = computed(() => customers.value.length > 0)

  const segmentDistribution = computed(() => segments.value.map(seg => ({
    ...seg,
    label: SEGMENT_LABELS[seg.name] || seg.label,
    color: SEGMENT_COLORS[seg.name] || seg.color,
  })))

  const vipCustomers = computed(() => customers.value.filter(c => c.segment === 'vip'))

  const atRiskCustomers = computed(() => customers.value.filter(c => c.segment === 'at_risk'))

  const dormantCustomers = computed(() => customers.value.filter(c => c.segment === 'dormant'))

  const fetchRfmAnalysis = async ({ start = null, end = null } = {}) => {
    loading.value = true
    try {
      const res = await CustomerApi.getRfmAnalysis({
        startDate: start,
        endDate: end,
      })

      startDate.value = res.data.period?.startDate
      endDate.value = res.data.period?.endDate
      summary.value = res.data.summary || {
        totalCustomers: 0,
        analyzedCustomers: 0,
        averageRfmScore: 0,
      }
      customers.value = res.data.customers || []
      segments.value = res.data.segments || []

      return {
        summary: summary.value,
        customers: customers.value,
        segments: segments.value,
      }
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return {
        summary: { totalCustomers: 0, analyzedCustomers: 0, averageRfmScore: 0 },
        customers: [],
        segments: [],
      }
    } finally {
      loading.value = false
    }
  }

  const getSegmentLabel = segmentName => SEGMENT_LABELS[segmentName] || segmentName

  const getSegmentColor = segmentName => SEGMENT_COLORS[segmentName] || '#9E9E9E'

  const getSegmentDescription = segmentName => SEGMENT_DESCRIPTIONS[segmentName] || ''

  const getScoreColor = score => {
    if (score >= 5) return '#4CAF50'
    if (score >= 4) return '#8BC34A'
    if (score >= 3) return '#FFC107'
    if (score >= 2) return '#FF9800'

    return '#F44336'
  }

  return {
    customers,
    summary,
    segments,
    loading,
    startDate,
    endDate,
    hasCustomers,
    segmentDistribution,
    vipCustomers,
    atRiskCustomers,
    dormantCustomers,
    fetchRfmAnalysis,
    getSegmentLabel,
    getSegmentColor,
    getSegmentDescription,
    getScoreColor,
    SEGMENT_LABELS,
    SEGMENT_COLORS,
    SEGMENT_DESCRIPTIONS,
  }
}
